import { QueryObserverResult } from 'react-query';
import { candidateService } from '../../../../api';
import {
  AccessArrangmentGetMedicalConditionsResponse,
  AccessArrangmentGetSelectedResponse,
  MedicalCondition,
  MedicalConditionAmendment,
} from '../../../../api/candidate/access-arrangement/model';
import { ServiceError } from '../../../../hooks/error';

interface IUseGetMedicalCondition {
  medicalConditionData: AccessArrangmentGetMedicalConditionsResponse | undefined;
  accessArrangementSubmittedData?: AccessArrangmentGetSelectedResponse | MedicalConditionAmendment;
  error: ServiceError | null;
}

const updateMedicalConditionData = (
  setDropdownReady: (dropdownReady: boolean) => void,
  medicalConditions?: MedicalCondition[],
  medicalConditionData?: AccessArrangmentGetMedicalConditionsResponse
) => {
  if (medicalConditions?.length) {
    medicalConditions.forEach((medicalCondition) => {
      if (medicalConditionData?.every((condition) => condition.uuid !== medicalCondition.uuid)) {
        medicalConditionData?.push(medicalCondition);
      }
    });

    medicalConditionData?.sort((first, second) => first.name.localeCompare(second.name));
  }
  setDropdownReady(true);
};

export const useGetMedicalCondition = (
  uuid: string,
  setData: (
    medicalConditionDraftModel: AccessArrangmentGetSelectedResponse | MedicalConditionAmendment,
    medicalConditionDropdown: AccessArrangmentGetMedicalConditionsResponse
  ) => void,
  setDropdownReady: (dropdownReady: boolean) => void,
  amendment = false
): IUseGetMedicalCondition => {
  const getAmendmentMedicalCondition = () => {
    const {
      data: accessArrangementSubmittedData,
      refetch,
    } = candidateService.accessArrangement.candidate.useGetMedicalConditionAmendment(
      uuid,
      (res) => {
        const { medicalConditions } = res.medicalConditionDraftModel.medicalConditionDropdownModel;

        updateMedicalConditionData(setDropdownReady, medicalConditions, medicalConditionData);
      }
    );

    const {
      data: medicalConditionData,
      error,
    } = candidateService.accessArrangement.candidate.useGetAppealArrangementMedicalConditionsAmendment(
      uuid,
      (medicalConditionDropdown) => {
        onSuccess(refetch, medicalConditionDropdown);
      }
    );

    return { medicalConditionData, accessArrangementSubmittedData, error };
  };

  const getMedicalCondition = () => {
    const {
      data: accessArrangementSubmittedData,
      refetch,
    } = candidateService.accessArrangement.candidate.useGetAppealArrangementSelectedConditions(
      uuid,
      (res) => {
        const { medicalConditions } = res.medicalConditionDropdownModel;

        updateMedicalConditionData(setDropdownReady, medicalConditions, medicalConditionData);
      }
    );

    const {
      data: medicalConditionData,
      error,
    } = candidateService.accessArrangement.candidate.useGetAppealArrangementMedicalConditions(
      (medicalConditionDropdown) => {
        onSuccess(refetch, medicalConditionDropdown);
      }
    );

    return { medicalConditionData, accessArrangementSubmittedData, error };
  };

  const onSuccess = (
    refetch: () => Promise<
      QueryObserverResult<
        MedicalConditionAmendment | AccessArrangmentGetSelectedResponse,
        ServiceError
      >
    >,
    medicalConditionDropdown: AccessArrangmentGetMedicalConditionsResponse
  ) => {
    refetch().then(({ data }) => {
      if (data) {
        setData(data, medicalConditionDropdown);
      }
    });
  };

  if (amendment) {
    return getAmendmentMedicalCondition();
  }

  return getMedicalCondition();
};
